import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Shield, Users, Eye, FileText, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import SEO from '../common/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';

const AdminPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { isDarkMode, isRTL } = useTheme();
  const { t } = useTranslation();

  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const checkAdmin = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/admin/check`, { headers: getAuthHeaders() });
        const data = await response.json();
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          fetchStats();
          fetchUsers();
        }
      } catch (err) {
        setError('Failed to verify admin access');
      } finally {
        setLoading(false);
      }
    };
    checkAdmin();
  }, [isAuthenticated, user]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, { headers: getAuthHeaders() });
      if (response.ok) setStats(await response.json());
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users?limit=20`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[var(--bg-dark-from)]' : 'bg-sky-50'}`}>
        <div className="w-8 h-8 border-4 border-[var(--p-hex)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <SEO title={t('admin.adminPanel')} path="/admin" />
        <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[var(--bg-dark-from)]' : 'bg-sky-50'}`} dir={isRTL ? 'rtl' : 'ltr'}>
          <div className={`text-center p-8 rounded-2xl ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-lg'}`}>
            <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
            <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Authentication Required</h2>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Please sign in to access the admin panel.</p>
          </div>
        </div>
      </>
    );
  }

  if (!isAdmin) {
    return (
      <>
        <SEO title={t('admin.adminPanel')} path="/admin" />
        <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[var(--bg-dark-from)]' : 'bg-sky-50'}`} dir={isRTL ? 'rtl' : 'ltr'}>
          <div className={`text-center p-8 rounded-2xl max-w-md ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-lg'}`}>
            <Shield className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
            <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Access Denied</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>You don't have permission to access this page.</p>
            {user && <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>Signed in as: {user.email}</p>}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO title={t('admin.adminPanel')} description="Admin dashboard for Never Miss A Prayer" path="/admin" />
      <div
        className={`min-h-screen ${
          isDarkMode
            ? 'bg-gradient-to-b from-[var(--bg-dark-from)] via-[var(--bg-dark-via)] to-[#0f172a]'
            : 'bg-gradient-to-b from-[var(--bg-light-from)] via-[var(--bg-light-via)] to-white'
        }`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="px-4 pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className={`w-6 h-6 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>
                {t('admin.adminPanel')}
              </h1>
            </div>
            <Button onClick={() => { fetchStats(); fetchUsers(); }} variant="outline" size="sm"
              className={isDarkMode ? 'border-white/20 text-white hover:bg-white/10' : ''}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Welcome, {user?.name}</p>
        </div>

        <div className="px-4 space-y-4 pb-24">
          {stats && (
            <div className="grid grid-cols-2 gap-4">
              <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/20' : 'bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Eye className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>{t('admin.totalVisits')}</span>
                </div>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>{stats.total_visits.toLocaleString()}</p>
              </div>

              <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/20' : 'bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Users className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>{t('admin.uniqueVisitors')}</span>
                </div>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>{stats.unique_visitors.toLocaleString()}</p>
              </div>

              <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/20' : 'bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Users className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{t('admin.registeredUsers')}</span>
                </div>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>{stats.total_users.toLocaleString()}</p>
              </div>

              <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/20' : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>Prayer Logs</span>
                </div>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>{stats.total_prayer_logs.toLocaleString()}</p>
              </div>

              <div className={`col-span-2 rounded-2xl p-4 ${isDarkMode ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/20' : 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{t('admin.totalPrayersCompleted')}</span>
                </div>
                <p className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>{(stats.total_prayers_completed || 0).toLocaleString()}</p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-green-400/70' : 'text-green-600/70'}`}>{t('admin.byAllUsers')}</p>
              </div>
            </div>
          )}


          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>{t('admin.recentUsers')}</h2>
            <div className="space-y-3">
              {users.length === 0 ? (
                <p className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No users yet</p>
              ) : (
                users.map((u, idx) => (
                  <div key={u.user_id || idx} className={`flex items-center gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
                        <Users className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{u.name}</p>
                      <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{u.email}</p>
                    </div>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPage;
